import type { ParsedUrlQuery } from 'querystring';

/**
 * Filters out route slugs from a `router.query` object by comparing it with `URLSearchParams`.
 *
 * This utility is essential in environments where route slugs (from the URL path)
 * and query parameters are combined into a single object (e.g., Next.js Pages Router),
 * and you need to isolate only the query parameters. It leverages the behavior of
 * `URLSearchParams` which explicitly excludes slugs.
 *
 * @param {ParsedUrlQuery} routerQuery The `router.query` object containing both slugs and query params.
 * @param {URLSearchParams} searchParams The `URLSearchParams` object, typically from the `useSearchParams` hook.
 * @returns {ParsedUrlQuery} A new object containing only the query parameters.
 *
 * @example
 * // URL: /vaults/[vaultId]/portfolio?filter=active
 * // In a component or hook:
 * const router = useRouter();
 * const searchParams = useSearchParams();
 * * const routerQuery = router.query; // { vaultId: '12345', filter: 'active' }
 * const searchParamsObj = searchParams; // URLSearchParams { 'filter' => 'active' }
 * * const cleanParams = getCleanQueryParams(routerQuery, searchParamsObj);
 * // cleanParams will be: { filter: 'active' }
 *
 * @example
 * // URL: /assets/[assetId]?filter=crypto&sortBy=price
 * // In a component or hook:
 * const router = useRouter();
 * const searchParams = useSearchParams();
 *
 * const routerQuery = router.query; // { assetId: 'btc-bitcoin', filter: 'crypto', sortBy: 'price' }
 * const searchParamsObj = searchParams; // URLSearchParams { 'filter' => 'crypto', 'sortBy' => 'price' }
 *
 * const cleanParams = getCleanQueryParams(routerQuery, searchParamsObj);
 * // cleanParams will be: { filter: 'crypto', sortBy: 'price' }
 */
export const getCleanQueryParams = (
  routerQuery: ParsedUrlQuery,
  searchParams: URLSearchParams,
): ParsedUrlQuery => {
  if (!routerQuery) {
    return {};
  }

  const paramKeys = new Set(searchParams.keys());

  // Object.entries() returns an array of [key, value] pairs.
  // We filter this array to keep only entries where the key exists in our paramKeys Set.
  // Object.fromEntries() converts the filtered array back into a plain object.
  return Object.fromEntries(
    Object.entries(routerQuery).filter(([key]) => paramKeys.has(key)),
  );
};
